import React, { useLayoutEffect, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import debug from 'debug';

const log = debug('rois:components:forms:budget-items');

if (import.meta.env.DEV) {
  debug.enable('rois:components:forms:budget-items*');
}

// Styles
import {
  AdviceContainer,
  ButtonContainer,
  ContainerError,
  GeneralContainer,
  TitleContainerWithButton,
} from '../styles';

// Components
import { Typography } from '../../../display/Typography';
import { BudgetItemSelector } from '../../../selectors/BudgetItemSelector';
import { ActionBudgetItemsTable } from '../../../display/tables/ActionBudgetItemsTable';
import SimpleBackgroundSwitch from '../../../inputs/buttons/SwitchButton/SimpleBackground';
import { MainButton } from '../../../inputs/buttons/MainButton';

// Types
import {
  BudgetItemsFormType,
  BudgetItemsFormValues,
  BudgetItemsTableData,
} from './types';
import { BudgetItemToAdd } from '../../../selectors/BudgetItemSelector/types';
import { Role } from '../../../../types/auth';

// Utils
import { useCacheFormInSessionStorage } from '../../../../hooks/useCacheForm';
import { handleAdd, handleDelete, handleUpdate } from '../formTableUtils';
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../utils/sessionStorage';
import { DataState } from '../types';
import { OrderState } from '../../../../types/orders';

// Hooks
import { useAuth } from '../../../../hooks/useAuth';
import { roleCanViewBudgets } from '../../../../utils/rolePermissions';

const BudgetItemsForm = ({
  initialData,
  onFormSubmit,
  sessionStorageKey,
  updateKey,
  textButton,
}: BudgetItemsFormType) => {
  const { t } = useTranslation(['workOrders', 'common', 'buttons', 'forms']);
  const { user } = useAuth();
  // ✅ Para activar validación visual al navegar desde modal de errores
  const locationState = useLocation();

  // Guard: Tablets y roles ciegos a budgets (capataz) no deben ver este componente
  if (
    user?.user?.rolActive === Role.Tablet ||
    !roleCanViewBudgets(user?.user?.rolActive)
  ) {
    return null;
  }

  // ✅ Debug logger - moved to module level for proper initialization
  const logBudgetItems = log;
  const { control, handleSubmit, setValue, register, getValues } =
    useForm<BudgetItemsFormValues>({
      defaultValues: {
        ...initialData,
        // ✅ FIX: Default a 'false' (No hay) para NewAction
        // Si initialData tiene valor (ActionsValidation o sessionStorage), se usará ese
        isBudgetItemsTableDisplayed: initialData.isBudgetItemsTableDisplayed
          ? initialData.isBudgetItemsTableDisplayed
          : 'false',
      },
    });

  const [dataFromTables, setDataFromTables] = useState<BudgetItemsTableData[]>(
    []
  );
  // ✅ FIX: Default a 'false' (No hay) para NewAction - si hay datos de servidor (ActionsValidation) se sobrescribirá
  const [switchState, setSwitchState] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const updateTableKey = 'budgetItemsData';

  useCacheFormInSessionStorage(sessionStorageKey, control);

  const switchStateValue = () => {
    const { isBudgetItemsTableDisplayed } = initialData;

    if (isBudgetItemsTableDisplayed) {
      return isBudgetItemsTableDisplayed === 'true';
    }

    // ✅ FIX: Default a 'false' (No hay) para NewAction
    setValue('isBudgetItemsTableDisplayed', 'false');
    return false;
  };

  useLayoutEffect(() => {
    logBudgetItems('🔍 [BudgetItemsForm] useLayoutEffect iniciado');
    logBudgetItems('📦 [BudgetItemsForm] initialData completo:', initialData);
    logBudgetItems(
      '📦 [BudgetItemsForm] initialData.budgetItemsTable:',
      initialData?.budgetItemsTable
    );
    logBudgetItems(
      '📦 [BudgetItemsForm] Object.keys(budgetItemsTable):',
      Object.keys(initialData?.budgetItemsTable ?? {})
    );
    logBudgetItems(
      '📦 [BudgetItemsForm] Object.values(budgetItemsTable):',
      Object.values(initialData?.budgetItemsTable ?? {})
    );

    // ✅ PATRÓN ESTÁNDAR: Seguir patrón Materials con budgetItemsTable
    const tableData = Object.values(initialData?.budgetItemsTable ?? {}).map(
      (item) => ({
        id: item.id || item.budgetItemId, // Fallback para compatibilidad
        budgetItemId: item.budgetItemId,
        code: item.code,
        description: item.description,
        unit: item.unit,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalAmount: item.totalAmount,
        category: item.category || {
          code: '',
          name: '',
          color: undefined,
        }, // ✅ Fallback para category requerido
      })
    );

    setDataFromTables(tableData);
    setSwitchState(switchStateValue());

    logBudgetItems(
      'BudgetItemsForm initialized with %d items',
      tableData.length
    );
  }, [initialData.budgetItemsTable]); // ✅ Cambiar dependencia a budgetItemsTable

  const handleChange = () => {
    if (switchState) {
      setSwitchState(false);
      setValue('isBudgetItemsTableDisplayed', 'false');
      setValue('budgetItemsTable', null);

      if (updateKey) {
        // Para ActionsValidation: marcar todos como eliminados
        const idsToDelete = dataFromTables.map((item) => item.id);

        const currentCacheData = sessionStorage.getItem(updateKey)
          ? JSON.parse(
              decompressSessionStorage(
                sessionStorage.getItem(updateKey) as string
              )
            )
          : {};

        currentCacheData[updateTableKey] ??= {};
        currentCacheData[updateTableKey][DataState.deleted] = idsToDelete;
        currentCacheData[updateTableKey][DataState.added] = [];
        currentCacheData[updateTableKey][DataState.updated] = [];

        sessionStorage.setItem(
          updateKey,
          compressSessionStorage(JSON.stringify(currentCacheData))
        );
      }
      setDataFromTables([]);
    } else {
      setSwitchState(true);
      setValue('isBudgetItemsTableDisplayed', 'true');
    }
  };

  const validateBudgetItemsTable = (tableValues: BudgetItemsTableData[]) => {
    const existAtLeastOneValue = tableValues && tableValues.length > 0;
    const allTheElementsHaveQuantity = tableValues?.every(
      (item) => item && item.quantity > 0
    );

    if (!existAtLeastOneValue) {
      setError('Afegiu almenys un element a la taula principal');
      return false;
    }

    if (!allTheElementsHaveQuantity) {
      setError('Si us plau, ompliu tots els camps dels elements afegits');
      return false;
    }

    setError(null);
    return true;
  };

  const checkTableValues = () => {
    if (switchState) {
      return validateBudgetItemsTable(dataFromTables);
    }
    return true;
  };

  // ✅ Activar validación visual al navegar desde modal de errores
  useEffect(() => {
    if (locationState.state?.validateOnMount && switchState) {
      log('🚨 Triggering validation on mount');
      checkTableValues();
    }
  }, [locationState.state?.validateOnMount, switchState, dataFromTables]);

  // Añadir partida
  const handleAddBudgetItem = (item: BudgetItemToAdd) => {
    log('➕ handleAddBudgetItem llamado:', item);
    log('➕ sessionStorageKey:', sessionStorageKey);
    log('➕ updateKey:', updateKey);

    const newItem: BudgetItemsTableData = {
      id: item.budgetItemId, // Usar budgetItemId como ID único
      budgetItemId: item.budgetItemId,
      code: item.code,
      description: item.description,
      unit: item.unit,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      totalAmount: item.quantity * item.unitPrice,
      category: item.category || {
        code: '',
        name: '',
        color: undefined,
      }, // ✅ Fallback para category requerido
    };

    log('➕ newItem creado:', newItem);

    const updatedItems = [...dataFromTables, newItem];
    setDataFromTables(updatedItems);

    log('➕ updatedItems (total):', updatedItems.length);

    // ✅ PATRÓN ESTÁNDAR: Actualizar form value como Materials (Record format)
    const updatedTable = updatedItems.reduce(
      (acc, item) => {
        acc[item.id] = item;
        return acc;
      },
      {} as Record<string, BudgetItemsTableData>
    );

    log('➕ updatedTable keys:', Object.keys(updatedTable));
    setValue('budgetItemsTable', updatedTable);
    log('✅ setValue("budgetItemsTable") ejecutado');

    // Para ActionsValidation: tracking de cambios
    if (updateKey) {
      log('➕ Llamando a handleAdd para tracking');
      handleAdd(
        item.budgetItemId,
        updateTableKey,
        updateKey,
        undefined,
        item.quantity
      );
    }

    log('✅✅✅ handleAddBudgetItem completado');
  };

  // Eliminar partida
  const handleRemoveBudgetItem = (id: string) => {
    const itemToRemove = dataFromTables.find((item) => item.id === id);
    logBudgetItems('🗑️ Eliminando partida:', { id, item: itemToRemove });

    const updatedItems = dataFromTables.filter((item) => item.id !== id);
    setDataFromTables(updatedItems);

    // ✅ PATRÓN ESTÁNDAR: Actualizar form value como Materials (Record format)
    const updatedTable = updatedItems.reduce(
      (acc, item) => {
        acc[item.id] = item;
        return acc;
      },
      {} as Record<string, BudgetItemsTableData>
    );

    setValue('budgetItemsTable', updatedTable);

    // Para ActionsValidation: tracking de cambios
    if (updateKey) {
      if (itemToRemove) {
        logBudgetItems('📝 Tracking eliminación en ActionsValidation');
        handleDelete(id, itemToRemove.id, updateTableKey, updateKey);
      }
    } else {
      logBudgetItems('💾 Eliminación en NewAction - solo sessionStorage');
    }

    logBudgetItems(
      `✅ Partida eliminada. Items restantes: ${updatedItems.length}`
    );
  };

  // Actualizar cantidad
  const handleUpdateQuantity = (id: string, quantity: number) => {
    logBudgetItems(
      '🔄 [handleUpdateQuantity] Iniciando actualización de cantidad'
    );
    logBudgetItems('🔄 [handleUpdateQuantity] ID:', id);
    logBudgetItems('🔄 [handleUpdateQuantity] Nueva cantidad:', quantity);
    logBudgetItems('🔄 [handleUpdateQuantity] updateKey:', updateKey);

    const updatedItems = dataFromTables.map((item) =>
      item.id === id
        ? {
            ...item,
            quantity,
            totalAmount: quantity * item.unitPrice,
          }
        : item
    );

    logBudgetItems('🔄 [handleUpdateQuantity] updatedItems:', updatedItems);
    setDataFromTables(updatedItems);

    // ✅ PATRÓN ESTÁNDAR: Actualizar form value como Materials (Record format)
    const updatedTable = updatedItems.reduce(
      (acc, item) => {
        acc[item.id] = item;
        return acc;
      },
      {} as Record<string, BudgetItemsTableData>
    );

    logBudgetItems('🔄 [handleUpdateQuantity] updatedTable:', updatedTable);
    setValue('budgetItemsTable', updatedTable);

    // Para ActionsValidation: tracking de cambios
    if (updateKey) {
      const item = dataFromTables.find((item) => item.id === id);
      logBudgetItems(
        '🔄 [handleUpdateQuantity] Item encontrado para tracking:',
        item
      );
      logBudgetItems(
        '🔄 [handleUpdateQuantity] initialData completo:',
        initialData
      );
      if (item) {
        logBudgetItems(
          '🔄 [handleUpdateQuantity] Llamando a handleUpdate con:'
        );
        logBudgetItems('  - quantity:', quantity);
        logBudgetItems('  - id:', id);
        logBudgetItems('  - unitId:', item.id);
        logBudgetItems('  - updateTableKey:', updateTableKey);
        logBudgetItems('  - updateKey:', updateKey);
        logBudgetItems(
          '  - initialData.dataFromServer:',
          initialData.dataFromServer
        );

        handleUpdate(
          quantity,
          id,
          item.id, // unitId
          initialData,
          updateTableKey,
          'budgetItemsTable',
          updateKey
        );

        logBudgetItems('✅ [handleUpdateQuantity] handleUpdate ejecutado');

        // Verificar qué se guardó en sessionStorage
        const sessionStorageKey = updateKey;
        const sessionStorageData = sessionStorage.getItem(sessionStorageKey);
        logBudgetItems(
          '🔍 [handleUpdateQuantity] SessionStorage después de handleUpdate:',
          sessionStorageData
        );
      }
    } else {
      logBudgetItems(
        'ℹ️ [handleUpdateQuantity] No hay updateKey - no se hace tracking'
      );
    }
  };

  // Preparar items existentes para el selector
  const existingItems = dataFromTables.map((item) => ({
    budgetItemId: item.budgetItemId,
    quantity: item.quantity,
  }));

  return (
    <GeneralContainer
      onSubmit={(e) => {
        if (checkTableValues()) {
          handleSubmit(onFormSubmit)(e);
        } else {
          e.preventDefault();
        }
      }}
    >
      <TitleContainerWithButton>
        <Typography variant="semiBold" component="h3" size="L">
          {t('workOrders:budgetItemsTab')}
        </Typography>
        <SimpleBackgroundSwitch
          changeState={handleChange}
          state={switchState}
          disabled={
            initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
            initialData?.parentExpeditionOrderStatus === OrderState.End ||
            initialData?.state === OrderState.Annulled
          }
        />
      </TitleContainerWithButton>

      {switchState && dataFromTables ? (
        <>
          <BudgetItemSelector
            onAdd={handleAddBudgetItem}
            existingItems={existingItems}
            disabled={false}
          />

          <ActionBudgetItemsTable
            items={dataFromTables}
            onRemove={handleRemoveBudgetItem} // ✅ SIEMPRE disponible (NewAction + ActionsValidation)
            onUpdateQuantity={handleUpdateQuantity}
            showPrices={true}
            isReadOnly={false}
          />
        </>
      ) : (
        <AdviceContainer>
          <Typography component="p" variant="semiBold" colorText="greyDark">
            {t('forms:budgetItems.noBudgetItems')}
          </Typography>
        </AdviceContainer>
      )}

      {error && switchState && (
        <ContainerError>
          <Typography component="p" size="S" colorText="lightCoralRed">
            {error}
          </Typography>
        </ContainerError>
      )}

      <ButtonContainer>
        <MainButton
          text={
            initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
            initialData?.parentExpeditionOrderStatus === OrderState.End ||
            initialData?.state === OrderState.Annulled
              ? t('buttons:continue')
              : textButton || t('buttons:continue')
          }
          type="submit"
        />
      </ButtonContainer>
    </GeneralContainer>
  );
};

export default BudgetItemsForm;
